import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import api from "../api";

const LatestWorksheetsCarousel = () => {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch latest worksheets
  useEffect(() => {
    const fetchLatestWorksheets = async () => {
      if (!auth.isAuthenticated || !auth.user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(
          "/api/worksheets/?ordering=-created_at&limit=10"
        );

        let worksheetData = [];
        if (response.data && Array.isArray(response.data)) {
          worksheetData = response.data;
        } else if (
          response.data &&
          response.data.results &&
          Array.isArray(response.data.results)
        ) {
          worksheetData = response.data.results;
        }

        setWorksheets(worksheetData.slice(0, 8)); // Limit to 8 for carousel
        setError("");
      } catch (error) {
        console.error("Error fetching latest worksheets:", error);
        setError("Unable to load recent worksheets");
        setWorksheets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestWorksheets();
  }, [auth.isAuthenticated, auth.user]);

  const handleWorksheetClick = (worksheetId) => {
    navigate(`/my-worksheets`, { state: { highlightId: worksheetId } });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return "Yesterday";
    return date.toLocaleDateString();
  };

  const carouselStyles = {
    container: {
      position: "sticky",
      bottom: 0,
      backgroundColor: "#f8f9fa",
      borderTop: "1px solid #dee2e6",
      padding: "1rem 0",
      marginTop: "2rem",
      zIndex: 10,
    },
    scrollContainer: {
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "nowrap",
      paddingBottom: "0.5rem",
      scrollbarWidth: "thin",
      scrollbarColor: "#6c757d #f8f9fa",
    },
    card: {
      minWidth: "280px",
      maxWidth: "280px",
      display: "inline-block",
      marginRight: "1rem",
      whiteSpace: "normal",
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      border: "1px solid #dee2e6",
      borderRadius: "0.5rem",
      backgroundColor: "#ffffff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    cardHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
    },
    title: {
      fontSize: "0.9rem",
      fontWeight: "600",
      marginBottom: "0.5rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    description: {
      fontSize: "0.8rem",
      color: "#6c757d",
      marginBottom: "0.5rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    metadata: {
      fontSize: "0.75rem",
      color: "#6c757d",
    },
  };

  if (!auth.isAuthenticated) return null;

  return (
    <div style={carouselStyles.container}>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <i className="bi bi-clock-history me-2"></i>
            Latest Worksheets
          </h5>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate("/my-worksheets")}
          >
            View All
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading recent worksheets...</span>
          </div>
        ) : error ? (
          <Alert variant="light" className="py-2 mb-0">
            <small>{error}</small>
          </Alert>
        ) : worksheets.length === 0 ? (
          <Alert variant="light" className="py-2 mb-0">
            <small>
              <i className="bi bi-info-circle me-1"></i>
              No worksheets created yet. Create your first worksheet using
              Lightning or Thunder Generator above!
            </small>
          </Alert>
        ) : (
          <div style={carouselStyles.scrollContainer}>
            {worksheets.map((worksheet) => (
              <div
                key={worksheet.id}
                style={carouselStyles.card}
                onClick={() => handleWorksheetClick(worksheet.id)}
                onMouseEnter={(e) => {
                  Object.assign(
                    e.currentTarget.style,
                    carouselStyles.cardHover
                  );
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                }}
              >
                <Card.Body className="p-3">
                  <div style={carouselStyles.title} title={worksheet.title}>
                    {worksheet.title}
                  </div>

                  {worksheet.description && (
                    <div
                      style={carouselStyles.description}
                      title={worksheet.description}
                    >
                      {worksheet.description}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge bg="primary" className="px-2 py-1">
                      {worksheet.subject}
                    </Badge>
                    {worksheet.topic && (
                      <Badge bg="secondary" className="px-2 py-1">
                        {worksheet.topic}
                      </Badge>
                    )}
                  </div>

                  <div style={carouselStyles.metadata}>
                    <div className="d-flex justify-content-between">
                      <span>
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(worksheet.created_at)}
                      </span>
                      {worksheet.question_codes && (
                        <span>
                          <i className="bi bi-list-ol me-1"></i>
                          {Array.isArray(worksheet.question_codes)
                            ? worksheet.question_codes.length
                            : worksheet.question_codes.split(",").length}{" "}
                          questions
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </div>
            ))}
          </div>
        )}

        <style jsx>{`
          div::-webkit-scrollbar {
            height: 6px;
          }
          div::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          div::-webkit-scrollbar-thumb {
            background: #6c757d;
            border-radius: 3px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #495057;
          }
        `}</style>
      </Container>
    </div>
  );
};

export default LatestWorksheetsCarousel;
